import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus , Building2} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CompanyFilters, CompanyFiltersData } from "@/components/companies/CompanyFilters";
import { CompaniesTable } from "@/components/companies/CompaniesTable";
import { CreateEditCompanyDialog } from "@/components/companies/CreateEditCompanyDialog";
import { DeleteCompanyDialog } from "@/components/companies/DeleteCompanyDialog";
import {
  Company,
  CreateCompanyData,
  fetchCompanies,
  createCompany,
  updateCompany,
  activateCompany,
  inactivateCompany,
  deleteCompany,
} from "@/services/company-service";

export default function Companies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [filters, setFilters] = useState<CompanyFiltersData>({});
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isCreateEditDialogOpen, setIsCreateEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { toast } = useToast();

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const data = await fetchCompanies(
        includeInactive,
        false,
        filters.nameLike,
        currentPage,
        filters.pageSize || 20
      );
      setCompanies(data);
    } catch (error) {
      toast({
        title: "Erro ao carregar empresas",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, [filters, includeInactive, currentPage]);

  const handleFilterChange = (newFilters: CompanyFiltersData) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleCreateNew = () => {
    setSelectedCompany(null);
    setIsCreateEditDialogOpen(true);
  };

  const handleEdit = (company: Company) => {
    setSelectedCompany(company);
    setIsCreateEditDialogOpen(true);
  };

  const handleDelete = (company: Company) => {
    setSelectedCompany(company);
    setIsDeleteDialogOpen(true);
  };

  const handleSave = async (data: CreateCompanyData, id?: string) => {
    try {
      if (id) {
        await updateCompany(id, data);
        toast({
          title: "Empresa atualizada!",
          description: "As alterações foram salvas com sucesso.",
        });
      } else {
        await createCompany(data);
        toast({
          title: "Empresa criada!",
          description: "A empresa foi cadastrada com sucesso.",
        });
      }
      loadCompanies();
    } catch (error) {
      toast({
        title: id ? "Erro ao atualizar empresa" : "Erro ao criar empresa",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleConfirmDelete = async (companyId: string) => {
    try {
      await deleteCompany(companyId);
      toast({
        title: "Empresa excluída",
        description: "A empresa foi removida com sucesso.",
      });
      loadCompanies();
    } catch (error) {
      toast({
        title: "Erro ao excluir empresa",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };

  const handleActivate = async (company: Company) => {
    try {
      await activateCompany(company.id);
      toast({
        title: "Empresa ativada",
        description: `${company.name} foi ativada com sucesso.`,
      });
      loadCompanies();
    } catch (error) {
      toast({
        title: "Erro ao ativar empresa",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };

  const handleInactivate = async (company: Company) => {
    try {
      await inactivateCompany(company.id);
      toast({
        title: "Empresa inativada",
        description: `${company.name} foi inativada com sucesso.`,
      });
      loadCompanies();
    } catch (error) {
      toast({
        title: "Erro ao inativar empresa",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
              <Building2 className="page-icon" />
              <h1 className="page-title">Empresas</h1>
            </div>
          <p className="page-subtitle">
            Gerencie as empresas cadastradas
          </p>
        </div>
        <Button size="sm" onClick={handleCreateNew} className="btn-header">
          <Plus className="btn-header-icon" />
          Nova Empresa
        </Button>
      </div>

      <CompanyFilters
        onFilterChange={handleFilterChange}
        includeInactive={includeInactive}
        onIncludeInactiveChange={setIncludeInactive}
      />
      <div className="space-y-2">
        <p className="page-counter">{companies.length} empresa(s) encontrada(s)</p>
        <CompaniesTable
          companies={companies}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onActivate={handleActivate}
          onInactivate={handleInactivate}
        />
        <div className="flex items-center justify-between border rounded-lg px-4 py-2 bg-card">
          <span className="page-counter">Página {currentPage}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="btn-header" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Anterior</Button>
            <Button size="sm" variant="outline" className="btn-header" onClick={() => setCurrentPage(p => p + 1)} disabled={companies.length < (filters.pageSize || 20)}>Próxima</Button>
          </div>
        </div>

        <CreateEditCompanyDialog
          open={isCreateEditDialogOpen}
          onOpenChange={setIsCreateEditDialogOpen}
          company={selectedCompany}
          onSave={handleSave}
        />

        <DeleteCompanyDialog
          company={selectedCompany}
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          onDelete={handleConfirmDelete}
        />
      </div>
    </div>
  );
}